import { html, TemplateResult, CSSResultGroup } from 'lit';
import { customElement, query, property } from 'lit/decorators.js';
import componentStyles from './outline-splide-slider.css.lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import Splide from '@splidejs/splide';

/**
 * The Outline Splide Slider
 *
 * @element outline-splide-slider
 * @slot - default slot for any slider content
 */
@customElement('outline-splide-slider')
export class OutlineSplideSlider extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  /**
   * Label for the Previous button. Keep in mind multilingual support.
   */
  @property({ type: String, reflect: true, attribute: 'previous-label' })
  previousLabel: string;

  /**
   * Label for the Next button. Keep in mind multilingual support.
   */
  @property({ type: String, reflect: true, attribute: 'next-label' })
  nextLabel: string;

  /**
   * Height used for small screens.
   */
  @property({ type: String, reflect: true, attribute: 'height-sm' })
  heightSmall = '300px';

  /**
   * Height used for medium screens.
   */
  @property({ type: String, reflect: true, attribute: 'height-md' })
  heightMedium = '450px';

  /**
   * Height used for large screens. This is the one used as default.
   */
  @property({ type: String, reflect: true, attribute: 'height-lg' })
  heightLarge = '600px';

  @query('.splide')
  splideEl: HTMLElement;

  @query('.splide__arrow--prev')
  prevBtn: HTMLElement;

  @query('.splide__arrow--next')
  nextBtn: HTMLElement;

  splide: Splide;

  observer: MutationObserver;

  connectedCallback() {
    super.connectedCallback();
    this.observer = new MutationObserver(() => {
      this.requestUpdate();
    });
    this.observer.observe(this, { childList: true });
  }

  firstUpdated() {
    const splideTrack = this.shadowRoot?.querySelector(
      '.splide__track'
    ) as HTMLElement;
    const splideList = document.createElement('div');
    splideList.classList.add('splide__list');

    Array.from(this.children).forEach((item, idx, items) => {
      // remove item from lightdom rightaway to avoid browser repainting while processing
      splideList.appendChild(item);

      // process slide items
      item.classList.add('splide__slide', 'splide__slide__container');
      // item.setAttribute('role', 'group'); // probably it's better to move this to Drupal
      item.setAttribute('aria-label', `slide ${idx + 1} of ${items.length}`); // probably it's better to move this to Drupal
    });

    // append our elements to our shadowdom.
    splideTrack.appendChild(splideList);

    const splideConfig = {
      keyboard: true,
      arrows: true,
      pagination: true,
      height: '600px', // default large size
      breakpoints: {
        640: {
          // from this size and below
          height: '283px',
        },
        1024: {
          height: '454px',
        },
      },
      focusableNodes:
        'a, button, textarea, input, select, iframe, idb-button, idb-button a',
    };

    if (this.heightLarge) {
      splideConfig.height = this.heightLarge;
    }

    if (this.heightMedium) {
      splideConfig.breakpoints['1024'].height = this.heightMedium;
    }

    if (this.heightSmall) {
      splideConfig.breakpoints['640'].height = this.heightSmall;
    }

    this.splide = new Splide(this.splideEl, splideConfig).mount();

    // set aria-label of buttons for screen readers
    if (this.previousLabel) {
      this.prevBtn.setAttribute('aria-label', this.previousLabel);
    }
    if (this.nextLabel) {
      this.nextBtn.setAttribute('aria-label', this.nextLabel);
    }
  }

  disconnectedCallback() {
    this.observer.disconnect();
  }

  render(): TemplateResult {
    return html`
      <div class="splide" data-splide='{"":"","rewind":"true","rewindByDrag":"true","autoplay":"true","pauseOnHover":"true","pauseOnFocus":"true", "drag":"true","snap":"true"}'>
        <div class="splide__track">
        </div>
      </div>
    `;
  }
}
